import {
  LuAward,
  LuBookOpen,
  LuChartLine,
  LuCircleHelp,
  LuContact,
  LuFolderOpen,
  LuGlobe,
  LuHouse,
  LuLayers,
  LuLayoutGrid,
  LuList,
  LuToggleLeft,
  LuMail,
  LuIdCard,
  LuPalette,
  LuPlus,
  LuServer,
  LuSettings,
  LuShare2,
  LuSmartphone,
  LuStar,
  LuTrendingUp,
  LuUser,
  LuUsers,
  LuWrench,
} from 'react-icons/lu';

import { portfolioOverviewFields, projectDetailFields, serviceDetailFields, serviceOverviewFields } from './resources';
import { getPortfolioDetailPath, normalizeSlug, projectMatchesRoute } from '../../utils/portfolioMatching';

const fullAccess = { create: true, update: true, delete: true };
const updateDelete = { create: false, update: true, delete: true };
const updateOnly = { create: false, update: true, delete: false };

/**
 * Admin sidebar structure mirrors the public website layout.
 * Each item maps to a CRUD module with per-section access rules.
 */
export const adminNavigation = [
  {
    key: 'home',
    title: 'Home',
    icon: LuHouse,
    defaultOpen: true,
    items: [
      {
        key: 'hero-sections',
        label: 'Hero',
        icon: LuLayers,
        access: updateDelete,
        singleRecord: true,
        publicPath: '/#home',
        sectionHint: 'Update the hero banner shown at the top of the homepage.',
      },
      {
        key: 'domain-extensions',
        label: 'Domain Search',
        icon: LuGlobe,
        access: fullAccess,
        publicPath: '/#home',
        sectionHint: 'Manage domain extensions and prices shown in the domain search section.',
      },
      {
        key: 'projects',
        label: 'Featured Project',
        icon: LuAward,
        query: { featured: '1' },
        access: updateDelete,
        singleRecord: true,
        publicPath: '/#home',
        sectionHint: 'Edit the featured project showcase on the homepage.',
      },
      {
        key: 'partners',
        label: 'Partners',
        icon: LuUsers,
        access: fullAccess,
        publicPath: '/#home',
      },
      {
        key: 'about-company',
        label: 'About Company',
        icon: LuBookOpen,
        access: updateDelete,
        singleRecord: true,
        publicPath: '/#about',
        sectionHint: 'Update the About Company section content.',
      },
      {
        key: 'faqs',
        label: 'Why Choose Us',
        icon: LuCircleHelp,
        access: fullAccess,
        publicPath: '/#home',
        sectionHint: 'Manage feature cards in Why Choose Us. Title = card title, Description = card text, Icon = card icon.',
      },
      {
        key: 'service-overviews',
        label: 'Services Overview',
        icon: LuLayoutGrid,
        access: fullAccess,
        publicPath: '/#services',
        fields: serviceOverviewFields,
        sectionHint:
          'Manage homepage service cards only. Changes here do not affect individual service detail pages.',
      },
      {
        key: 'portfolio-overviews',
        label: 'Portfolio Overview',
        icon: LuFolderOpen,
        access: fullAccess,
        publicPath: '/#portfolio',
        fields: portfolioOverviewFields,
        sectionHint:
          'Manage homepage portfolio cards only. Full project pages are edited separately under Portfolio in the sidebar.',
      },
      {
        key: 'testimonials',
        label: 'Testimonials',
        icon: LuStar,
        access: fullAccess,
        publicPath: '/#home',
      },
      {
        key: 'contact-information',
        label: 'Contact',
        icon: LuContact,
        access: fullAccess,
        singleRecord: true,
        publicPath: '/#contact',
        sectionHint: 'Update contact details shown on the website.',
      },
      {
        key: 'footer-content',
        label: 'Footer',
        icon: LuList,
        access: updateOnly,
        singleRecord: true,
        publicPath: '/',
        sectionHint: 'Update footer logo, social links, quick links, and legal links.',
      },
    ],
  },
  {
    key: 'services',
    title: 'Services',
    icon: LuWrench,
    defaultOpen: true,
    items: [
      {
        key: 'services',
        label: 'Branding Services',
        icon: LuPalette,
        query: { slug: 'branding' },
        access: updateDelete,
        singleRecord: true,
        fields: serviceDetailFields,
        publicPath: '/services/branding',
        sectionHint: 'Edit this service detail page only. Homepage cards are managed separately under Home → Services Overview.',
      },
      {
        key: 'services',
        label: 'Mobile App Development',
        icon: LuSmartphone,
        query: { slug: 'mobile-app' },
        access: updateDelete,
        singleRecord: true,
        fields: serviceDetailFields,
        publicPath: '/services/mobile-app',
        sectionHint: 'Edit this service detail page only. Homepage cards are managed separately under Home → Services Overview.',
      },
      {
        key: 'services',
        label: 'Web Development',
        icon: LuWrench,
        query: { slug: 'web-development' },
        access: updateDelete,
        singleRecord: true,
        fields: serviceDetailFields,
        publicPath: '/services/web-development',
        sectionHint: 'Edit this service detail page only. Homepage cards are managed separately under Home → Services Overview.',
      },
      {
        key: 'services',
        label: 'Web Hosting',
        icon: LuServer,
        query: { slug: 'web-hosting' },
        access: updateDelete,
        singleRecord: true,
        fields: serviceDetailFields,
        publicPath: '/services/web-hosting',
        sectionHint: 'Edit this service detail page only. Homepage cards are managed separately under Home → Services Overview.',
      },
      {
        key: 'services',
        label: 'Digital Marketing',
        icon: LuTrendingUp,
        query: { slug: 'marketing' },
        access: updateDelete,
        singleRecord: true,
        fields: serviceDetailFields,
        publicPath: '/services/marketing',
        sectionHint: 'Edit this service detail page only. Homepage cards are managed separately under Home → Services Overview.',
      },
      {
        key: 'services',
        label: 'Data Analysis',
        icon: LuChartLine,
        query: { slug: 'data-analysis' },
        access: updateDelete,
        singleRecord: true,
        fields: serviceDetailFields,
        publicPath: '/services/data-analysis',
        sectionHint: 'Edit this service detail page only. Homepage cards are managed separately under Home → Services Overview.',
      },
    ],
  },
];

export const adminPortfolioNavigation = {
  key: 'portfolio',
  title: 'Portfolio',
  icon: LuFolderOpen,
  defaultOpen: true,
};

export const portfolioProjectNavDefaults = {
  key: 'projects',
  icon: LuFolderOpen,
  access: updateDelete,
  singleRecord: true,
  fields: projectDetailFields,
  sectionHint: 'Edit this portfolio detail page. Homepage cards are managed under Home → Portfolio Overview.',
};

export function createAddPortfolioNavItem() {
  return {
    key: 'projects',
    label: 'Add Project',
    icon: LuPlus,
    query: { portfolio: '1' },
    access: fullAccess,
    fields: projectDetailFields,
    publicPath: '/#portfolio',
    sectionHint:
      'Create a new portfolio project. It will appear in the Portfolio menu after save.',
  };
}

export function getProjectNavSlug(project) {
  return normalizeSlug(project.slug) || String(project.id);
}

export function projectMatchesSlug(project, slug) {
  return projectMatchesRoute(project, slug);
}

export function createPortfolioNavItem(project) {
  const slug = getProjectNavSlug(project);
  return {
    ...portfolioProjectNavDefaults,
    label: project.title || slug,
    query: { slug },
    publicPath: project.slug ? getPortfolioDetailPath(project) : `/portfolio/${project.id}`,
  };
}

export const adminStandaloneItems = [
  {
    key: 'messages',
    label: 'Messages',
    icon: LuMail,
    access: { create: false, update: true, delete: true },
    sectionHint:
      'Incoming messages from the website contact form. Admin cannot send messages from here.',
  },
  { key: 'blogs', label: 'Blogs', icon: LuGlobe },
  {
    key: 'employee-verification',
    label: 'Employee Verification',
    icon: LuIdCard,
    sectionHint: 'Upload employee ID cards and approve public QR verification.',
  },
];

export const adminSettingsNavigation = {
  key: 'settings',
  title: 'Settings',
  icon: LuSettings,
  defaultOpen: true,
  items: [
    {
      key: 'admin-users',
      label: 'Users',
      icon: LuUsers,
      access: fullAccess,
      sectionHint: 'Create and manage admin accounts that can sign in to the panel.',
    },
    {
      key: 'profile-settings',
      label: 'Profile Change',
      icon: LuUser,
      sectionHint: 'Update your profile photo, name, email, and password.',
    },
    {
      key: 'social-media-links',
      label: 'Social Media Links',
      icon: LuShare2,
      access: updateOnly,
      singleRecord: true,
      sectionHint: 'Manage social media profile URLs used across the website.',
    },
    {
      key: 'company-colors',
      label: 'Company Colors',
      icon: LuPalette,
      access: updateOnly,
      singleRecord: true,
      sectionHint: 'Manage company brand colors: Primary, Secondary, Black, and White.',
    },
    {
      key: 'website-sections',
      label: 'Website Sections',
      icon: LuToggleLeft,
      sectionHint: 'Enable or disable homepage sections on the public website.',
    },
  ],
};

export function buildModulePath(item) {
  const params = new URLSearchParams();
  if (item.query) {
    Object.entries(item.query).forEach(([key, value]) => params.set(key, value));
  }
  const query = params.toString();
  return `/admin/modules/${item.key}${query ? `?${query}` : ''}`;
}

export function findNavItemByPath(pathname, search) {
  const resourceKey = pathname.replace('/admin/modules/', '');
  const params = new URLSearchParams(search);

  for (const group of adminNavigation) {
    for (const item of group.items) {
      if (item.key !== resourceKey) continue;

      if (item.query) {
        const matches = Object.entries(item.query).every(([key, value]) => params.get(key) === value);
        if (!matches) continue;
      } else if ([...params.keys()].length > 0) {
        continue;
      }

      return { group: group.title, item };
    }
  }

  if (resourceKey === 'projects') {
    const slug = params.get('slug');
    const featured = params.get('featured');
    const portfolio = params.get('portfolio');

    if (slug && featured !== '1') {
      return {
        group: adminPortfolioNavigation.title,
        item: {
          ...portfolioProjectNavDefaults,
          label: slug.replace(/-/g, ' '),
          query: { slug },
          publicPath: `/portfolio/${slug}`,
        },
      };
    }

    if (portfolio === '1') {
      return {
        group: adminPortfolioNavigation.title,
        item: createAddPortfolioNavItem(),
      };
    }
  }

  for (const item of adminStandaloneItems) {
    if (item.key !== resourceKey) continue;
    if ([...params.keys()].length > 0) continue;
    return { group: null, item };
  }

  for (const item of adminSettingsNavigation.items) {
    if (item.key !== resourceKey) continue;
    if ([...params.keys()].length > 0) continue;
    return { group: adminSettingsNavigation.title, item };
  }

  return null;
}

export function getPageTitle(pathname, search) {
  const match = findNavItemByPath(pathname, search);
  if (match) return match.item.label;

  const resourceKey = pathname.replace('/admin/modules/', '');
  return resourceKey.replace(/-/g, ' ');
}

export function getModuleConfig(pathname, search, getResourceByKey) {
  const resourceKey = pathname.replace('/admin/modules/', '');
  const resource = getResourceByKey(resourceKey);
  if (!resource) return null;

  const match = findNavItemByPath(pathname, search);
  const navItem = match?.item;

  return {
    ...resource,
    label: navItem?.label || resource.label,
    fields: navItem?.fields || resource.fields,
    access: navItem?.access || resource.access || fullAccess,
    publicPath: navItem?.publicPath || resource.publicPath,
    sectionHint: navItem?.sectionHint || resource.sectionHint,
    singleRecord: navItem?.singleRecord ?? resource.singleRecord ?? false,
    inbox: navItem?.inbox ?? resource.inbox ?? false,
    defaultValues: { ...(resource.defaultValues || {}), ...(navItem?.defaultValues || {}) },
    navQuery: navItem?.query || {},
  };
}
